import { AltiusRes } from "store/types";

import { AsyncActions } from "../base";

// ---------------------------------------------------------------------------
// Types — aligned with API contract
// ---------------------------------------------------------------------------

/** A single allocation record returned by GET /client-entity-allocations. */
export interface IClientEntityAllocation {
  id: number;
  client_id: number;
  investor_id: number;
  investor_name: string;
  entity_type: string | null;
  allocation_percentage: number;
  linked_deals_count: number;
  is_shared: boolean;
}

/** An investor available for allocation, returned by GET /available-investors. */
export interface IAvailableInvestor {
  id: number;
  name: string;
  entity_type: string | null;
  shared_client_count: number;
}

/** Payload for POST /client-entity-allocations. */
export interface ICreateAllocationPayload {
  client_id: number;
  investor_id: number;
  allocation_percentage: number;
}

/** Payload for PATCH /client-entity-allocations/{id}. */
export interface IUpdateAllocationPayload {
  allocation_percentage: number;
}

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface IClientEntityAllocationActions {
  getAllocations: (clientId: number) => Promise<IClientEntityAllocation[]>;
  getAvailableInvestors: (clientId: number) => Promise<IAvailableInvestor[]>;
  createAllocation: (payload: ICreateAllocationPayload) => Promise<IClientEntityAllocation>;
  updateAllocation: (allocationId: number, payload: IUpdateAllocationPayload) => Promise<IClientEntityAllocation>;
  deleteAllocation: (allocationId: number) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export class ClientEntityAllocationActions
  extends AsyncActions
  implements IClientEntityAllocationActions
{
  /**
   * Fetch all entity allocations for a given client.
   * GET /api/v0.0.3/client-entity-allocations?client_id={clientId}
   */
  public getAllocations: IClientEntityAllocationActions["getAllocations"] = async (clientId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IClientEntityAllocation[]>>({
      url: "/api/v0.0.3/client-entity-allocations",
      signal: this.addProcess("getAllocations").signal,
      params: { client_id: clientId }
    });
    this.removeProcess("getAllocations");
    return data;
  };

  /**
   * Fetch investors that can still be linked for this client.
   * GET /api/v0.0.3/client-entity-allocations/available-investors?client_id={clientId}
   */
  public getAvailableInvestors: IClientEntityAllocationActions["getAvailableInvestors"] = async (clientId) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<IAvailableInvestor[]>>({
      url: "/api/v0.0.3/client-entity-allocations/available-investors",
      signal: this.addProcess("getAvailableInvestors").signal,
      params: { client_id: clientId }
    });
    this.removeProcess("getAvailableInvestors");
    return data;
  };

  /**
   * Link a new entity allocation.
   * POST /api/v0.0.3/client-entity-allocations
   */
  public createAllocation: IClientEntityAllocationActions["createAllocation"] = async (payload) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IClientEntityAllocation>, ICreateAllocationPayload>({
      url: "/api/v0.0.3/client-entity-allocations",
      signal: this.addProcess("createAllocation").signal,
      data: payload
    });
    this.removeProcess("createAllocation");
    return data;
  };

  /**
   * Update an allocation's percentage.
   * PATCH /api/v0.0.3/client-entity-allocations/{allocationId}
   */
  public updateAllocation: IClientEntityAllocationActions["updateAllocation"] = async (
    allocationId,
    payload
  ) => {
    const {
      data: { data }
    } = await this.api.patch<AltiusRes<IClientEntityAllocation>, IUpdateAllocationPayload>({
      url: `/api/v0.0.3/client-entity-allocations/${allocationId}`,
      signal: this.addProcess("updateAllocation").signal,
      data: payload
    });
    this.removeProcess("updateAllocation");
    return data;
  };

  /**
   * Remove an entity allocation.
   * DELETE /api/v0.0.3/client-entity-allocations/{allocationId}
   */
  public deleteAllocation: IClientEntityAllocationActions["deleteAllocation"] = async (allocationId) => {
    await this.api.delete<AltiusRes<null>>({
      url: `/api/v0.0.3/client-entity-allocations/${allocationId}`,
      signal: this.addProcess("deleteAllocation").signal
    });
    this.removeProcess("deleteAllocation");
  };
}
